import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoCloseSharp } from 'react-icons/io5';
import { Button, CustomInput, CustomSpinner } from '@components';
import {
	AppString,
	toastSuccessHelper,
	validateEmail,
	validatePassword,
} from '@utils';
import { loginUser } from '@services';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from 'react-redux';
import { setIsUserLoggedInHandler } from '@/store/slice';

interface HandleSignInPopupProps {
	closeModal: () => void;
	toggleSignup: () => void;
	modelref: React.RefObject<HTMLDivElement>;
}

export const LoginScreen: React.FC<HandleSignInPopupProps> = ({
	modelref,
	closeModal,
	toggleSignup,
}) => {
	const [email, setEmail] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [emailError, setEmailError] = useState<string>('');
	const [passwordError, setPasswordError] = useState<string>('');
	const [responseError, setResponseError] = useState<string>('');
	const [lodder, setLodder] = useState<boolean>(false);
	const dispatch = useDispatch();

	const validateField = (
		value: string,
		validationFunc: (value: string) => string,
		setError: React.Dispatch<React.SetStateAction<string>>
	) => {
		const error = validationFunc(value);
		setError(error);
		return !error;
	};

	const validateForm = () => {
		setResponseError('');
		const isEmailValid = validateField(email, validateEmail, setEmailError);
		const isPasswordValid = validateField(
			password,
			validatePassword,
			setPasswordError
		);
		return isEmailValid && isPasswordValid;
	};

	const handleSubmit = async () => {
		if (validateForm()) {
			setLodder(true);
			try {
				const response = await loginUser({ email, password });
				if (response.status === 200) {
					const data = response?.data?.data;
					localStorage.setItem(AppString.email_id, data.email);
					localStorage.setItem(AppString.user_token, data.token);
					localStorage.setItem(
						AppString.full_name,
						`${data.fistName} ${data.lastName}`
					);
					dispatch(setIsUserLoggedInHandler(true));
					// Show success toast notification
					toastSuccessHelper('Login successful!');
					closeModal();
				}
			} catch (e: any) {
				setResponseError(e?.response?.data?.message || 'Something went wrong');
			}
			setLodder(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
			<div className="min-h-screen w-full py-8 px-4 flex items-center justify-center">
				<div
					className="flex flex-col justify-center  bg-[#fff] shadow-custom  md:p-4 w-[90%] max-w-[500px] border border-light-gray overflow-y-auto max-h-[90vh]"
					ref={modelref}
				>
					<div className="inline-flex justify-between items-center py-[1rem] px-4 mb-6 border-b ">
						<h2 className="font-semibold  text-[22px]">Login</h2>
						<button onClick={closeModal}>
							<IoCloseSharp className="w-5 h-5 font-bold" />
						</button>
					</div>
					<div className="text-[12px] px-4">
						<p className="mb-2">
							Log in to your account to bookmark properties, save custom
							searches, and set up notifications for new data results.
						</p>
						<p className="mb-2">
							If you do not have an account yet, <b>sign up</b> for one now.
						</p>
					</div>
					{responseError && (
						<div>
							<p className="text-red-600 font-semibold text-[14px] text-center">
								{responseError}
							</p>
						</div>
					)}
					<div className="px-4">
						<CustomInput
							label="Email"
							type="email"
							labelClassName="font-semibold"
							placeHolder={''}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							error={emailError}
						/>

						<CustomInput
							label="Password"
							type="password"
							labelClassName="mt-4 font-semibold"
							placeHolder={''}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							error={passwordError}
						/>
					</div>

					<div className="flex justify-between px-4 text-[16px] items-center   mb-10 mt-3">
						<div>
							<a
								className="font-normal text-blue-dark cursor-pointer  underline"
								href={`${process.env.REACT_APP_BACKEND_BASE_URL}/password-reset`}
								rel="noopener noreferrer"
								target="_blank"
							>
								Reset Password
							</a>
						</div>
						<div className=" px-4 border-b min-w-[200px]">
							<Button
								title="Submit"
								onPress={handleSubmit}
								styleDiv="self-center w-full text-center btn-dark p-2 rounded-sm cursor-pointer "
							/>
						</div>
					</div>

					<div className="px-4 pb-4">
						<Button
							title="Register for an account"
							onPress={toggleSignup}
							styleDiv="self-center w-full text-center btn-dark p-2 rounded-sm cursor-pointer"
						/>
					</div>
				</div>
			</div>

			{lodder && (
				<div className="absolute inset-0 flex items-center h-full justify-center bg-black bg-opacity-30">
					<CustomSpinner width={100} height={100} border={10} />
				</div>
			)}
		</div>
	);
};
